import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Copy } from '@phosphor-icons/react';

/**
 * BulkSessionForm - Form for creating multiple recurring sessions
 */
export function BulkSessionForm({
  open,
  onOpenChange,
  formData,
  setFormData,
  locations,
  onSubmit
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" className="flex-shrink-0" data-testid="bulk-session-btn">
          <Copy size={18} weight="bold" className="mr-2" />
          Bulk Create
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-border max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">CREATE RECURRING SESSIONS</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label>Location</Label>
            <Select 
              value={formData.location_id} 
              onValueChange={(v) => setFormData({...formData, location_id: v})}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select location" />
              </SelectTrigger>
              <SelectContent>
                {locations.map((loc) => (
                  <SelectItem key={loc.location_id} value={loc.location_id}>
                    {loc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label>Start Date</Label>
            <Input
              type="date"
              value={formData.start_date}
              onChange={(e) => setFormData({...formData, start_date: e.target.value})}
              required
              className="mt-1"
            />
          </div>
          
          <div>
            <Label>Start Time</Label>
            <Input
              type="time"
              value={formData.start_time}
              onChange={(e) => setFormData({...formData, start_time: e.target.value})}
              required
              className="mt-1"
            />
          </div>
          
          <div>
            <Label>End Time</Label>
            <Input
              type="time"
              value={formData.end_time}
              onChange={(e) => setFormData({...formData, end_time: e.target.value})}
              required
              className="mt-1"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Recurrence</Label>
              <Select 
                value={formData.recurrence} 
                onValueChange={(v) => setFormData({...formData, recurrence: v})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label># Sessions</Label>
              <Input
                type="number"
                min="1"
                max="12"
                value={formData.num_sessions}
                onChange={(e) => setFormData({...formData, num_sessions: parseInt(e.target.value)})}
                required
                className="mt-1"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Capacity</Label>
              <Input
                type="number"
                min="1"
                value={formData.capacity}
                onChange={(e) => setFormData({...formData, capacity: parseInt(e.target.value)})}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label>Grade Level</Label>
              <Select 
                value={formData.grade_group} 
                onValueChange={(v) => setFormData({...formData, grade_group: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="elementary">Elementary (3rd-5th Grade)</SelectItem>
                  <SelectItem value="middle">Middle School (6th-8th Grade)</SelectItem>
                  <SelectItem value="high">High School (9th-12th Grade)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" className="flex-1 btn-primary">
              Create {formData.num_sessions} Sessions
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
